import { useState } from "react";
import { useTranslation } from "react-i18next";
import { ArrowRight, Menu, X } from "lucide-react";

import { brand, brandAssetPath } from "@shared/config";
import { useAuthSnapshot } from "@shared/lib/auth";
import { useLandingPath } from "@shared/hooks/use-site-feature-flags/useLandingPath";
import { cn } from "@shared/lib/utils";
import { Button } from "@shared/ui/button";
import { LanguageSwitcher } from "@shared/ui/language-switcher";
import { ThemeSwitcher } from "@shared/ui/theme-switcher";

import { localizedHref, localizedRoot, resolveLang } from "../lib/links";

interface NavItem {
  id: string;
  labelKey: string;
}

const NAV_ITEMS: NavItem[] = [
  { id: "ecosystem", labelKey: "header.nav.features" },
  { id: "process", labelKey: "header.nav.process" },
  { id: "knowledge", labelKey: "header.nav.knowledge" },
  { id: "faq", labelKey: "header.nav.faq" },
];

export function Header() {
  const { t, i18n } = useTranslation("landing");
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const lang = resolveLang(i18n.resolvedLanguage);
  const assetBase = import.meta.env.BASE_URL;
  const { isAuthenticated } = useAuthSnapshot();
  // "Open dashboard" goes to the first enabled feature, never a disabled page.
  const landingPath = useLandingPath();

  const closeMenu = () => setMobileMenuOpen(false);

  return (
    <header className="fixed start-0 end-0 top-0 z-50 border-b border-border/70 bg-background/90 backdrop-blur-xl">
      <nav
        className="mx-auto flex h-16 max-w-7xl items-center justify-between px-4 sm:px-6 lg:px-8"
        aria-label={t("header.ariaLabel")}
      >
        <a
          href={localizedRoot(lang)}
          className="flex min-w-0 items-center gap-3"
          onClick={closeMenu}
        >
          <img
            src={brandAssetPath(assetBase, brand.assets.logoIcon)}
            alt={t("header.logoAlt")}
            className="h-9 w-9 shrink-0 dark:hidden"
            width={36}
            height={36}
          />
          <img
            src={brandAssetPath(assetBase, brand.assets.logoDarkIcon)}
            alt={t("header.logoAlt")}
            className="hidden h-9 w-9 shrink-0 dark:block"
            width={36}
            height={36}
          />
          <span className="truncate text-sm font-semibold tracking-wide text-foreground sm:text-base">
            {t("header.brand")}
          </span>
        </a>

        <div className="hidden items-center gap-1 xl:flex">
          {NAV_ITEMS.map((item) => (
            <a
              key={item.id}
              href={`#${item.id}`}
              className="rounded-full px-4 py-2 text-sm font-medium text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
            >
              {t(item.labelKey)}
            </a>
          ))}
        </div>

        <div className="hidden shrink-0 items-center gap-2 xl:flex">
          <ThemeSwitcher triggerClass="h-11 min-h-[44px] w-[11.5rem] rounded-xl !px-3" />
          <LanguageSwitcher
            compact
            className="h-11 min-h-[44px] rounded-xl border border-border/70 bg-background px-3 text-foreground shadow-sm hover:bg-muted"
          />
          {isAuthenticated ? (
            <Button asChild className="h-11 whitespace-nowrap rounded-xl px-5">
              <a href={localizedHref(landingPath, lang)}>
                {t("header.cta.openDashboard", "Open dashboard")}
                <ArrowRight className="h-4 w-4 rtl:scale-x-[-1]" aria-hidden="true" />
              </a>
            </Button>
          ) : (
            <>
              <Button
                variant="ghost"
                asChild
                className="h-11 whitespace-nowrap rounded-xl px-4"
              >
                <a href={localizedHref("/login", lang)}>{t("header.cta.signIn")}</a>
              </Button>
              <Button asChild className="h-11 whitespace-nowrap rounded-xl px-5">
                <a href={localizedHref("/register", lang)}>
                  {t("header.cta.getStarted")}
                  <ArrowRight className="h-4 w-4 rtl:scale-x-[-1]" aria-hidden="true" />
                </a>
              </Button>
            </>
          )}
        </div>

        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="xl:hidden"
          aria-label={mobileMenuOpen ? t("header.cta.closeMenu") : t("header.cta.openMenu")}
          aria-expanded={mobileMenuOpen}
          aria-controls="landing-mobile-menu"
          onClick={() => setMobileMenuOpen((open) => !open)}
        >
          {mobileMenuOpen ? (
            <X className="h-5 w-5" aria-hidden="true" />
          ) : (
            <Menu className="h-5 w-5" aria-hidden="true" />
          )}
        </Button>
      </nav>

      <div
        id="landing-mobile-menu"
        className={cn(
          "overflow-hidden border-b border-border bg-background transition-[max-height] duration-200 xl:hidden",
          mobileMenuOpen ? "max-h-[34rem]" : "max-h-0",
        )}
      >
        <div className="mx-auto flex max-w-7xl flex-col gap-2 px-4 pb-5 pt-2 sm:px-6">
          {NAV_ITEMS.map((item) => (
            <a
              key={item.id}
              href={`#${item.id}`}
              onClick={closeMenu}
              className="flex min-h-11 items-center rounded-lg px-3 text-sm font-medium text-muted-foreground hover:bg-muted hover:text-foreground"
            >
              {t(item.labelKey)}
            </a>
          ))}
          <div className="flex items-center gap-2 border-t border-border pt-3">
            <ThemeSwitcher triggerClass="h-11 min-h-[44px] rounded-xl !px-3" />
            <LanguageSwitcher
              compact
              className="h-11 min-h-[44px] rounded-xl border border-border/70 bg-background px-3 shadow-sm"
            />
          </div>
          {isAuthenticated ? (
            <Button asChild className="w-full">
              <a href={localizedHref(landingPath, lang)} onClick={closeMenu}>
                {t("header.cta.openDashboard", "Open dashboard")}
              </a>
            </Button>
          ) : (
            <>
              <Button variant="outline" asChild className="w-full">
                <a href={localizedHref("/login", lang)} onClick={closeMenu}>
                  {t("header.cta.signIn")}
                </a>
              </Button>
              <Button asChild className="w-full">
                <a href={localizedHref("/register", lang)} onClick={closeMenu}>
                  {t("header.cta.getStarted")}
                </a>
              </Button>
            </>
          )}
        </div>
      </div>
    </header>
  );
}

export const LandingHeader = Header;
export default Header;
